import { GraphQLResult } from '@aws-amplify/api-graphql';
import { API, graphqlOperation } from 'aws-amplify';
import _ from 'lodash';
import {
  CreateEventInput,
  CreateEventMutation,
  CreateEventSessionAttendeesInput,
  CreateEventSessionAttendeesMutation,
  CreateEventSessionFacilitatorInput,
  CreateEventSessionFacilitatorMutation,
  CreateEventSessionInput,
  CreateEventSessionMutation,
  EventSession,
  EventTypeEnum,
  GetEventSessionQuery,
  ListEventsByEventTypeQuery,
  UpdateEventInput,
  UpdateEventMutation,
  UpdateEventSessionAttendeesInput,
  UpdateEventSessionAttendeesMutation,
  UpdateEventSessionFacilitatorInput,
  UpdateEventSessionFacilitatorMutation,
  UpdateEventSessionInput,
  UpdateEventSessionMutation,
} from '../API';
import { listEventsByEventType, getEvent } from '../graphql/custom_queries';
import {
  createEvent,
  createEventSession,
  createEventSessionAttendees,
  createEventSessionFacilitator,
  updateEvent,
  updateEventSession,
  updateEventSessionAttendees,
  updateEventSessionFacilitator,
} from '../graphql/mutations';
import { addUserToChatChannel, getEventSession as getEventSessionQuery, getStripePaymentIntent, listEventSessionAttendeesByEventId } from '../graphql/queries';
import {
  CreateEventTimeInput,
  UpdateEventTimeInput,
  UpdateEventTimeMutation,
  CreateEventTimeMutation,
  CreateEventSessionTimeInput,
  UpdateEventSessionTimeInput,
  UpdateEventSessionTimeMutation,
  CreateEventSessionTimeMutation,
} from '../API';
import { updateEventTime, createEventTime, updateEventSessionTime, createEventSessionTime } from '../graphql/mutations';
import { GetEventQuery } from '../API';

export const getAllUpcomingEvents = async (eventType: EventTypeEnum, limit: number = 1) => {
  try {
    const req = (await API.graphql({
      query: listEventsByEventType,
      variables: { sortDirection: 'DESC', eventType: 'SMALL_GROUP', limit, filter: { startTimestamp: { gt: Math.round(Date.now() / 1000) } } },
      authMode: 'API_KEY',
    })) as GraphQLResult<ListEventsByEventTypeQuery>;
    return _.get(req, 'data.listEventsByEventType.items', []);
  } catch (err) {
    return err;
  }
};

export const getEventSession = async (eventSessionId: string) => {
  try {
    const req = (await API.graphql({
      query: getEventSessionQuery,
      variables: { id: eventSessionId },
      authMode: 'API_KEY',
    })) as GraphQLResult<GetEventSessionQuery>;
    return _.get(req, 'data.getEventSession', {});
  } catch (err) {
    return err;
  }
};

export const getEventById = async (id: string) => {
  try {
    const req = (await API.graphql({
      query: getEvent,
      variables: { id },
      authMode: 'API_KEY',
    })) as GraphQLResult<GetEventQuery>;
    return _.get(req, 'data.getEvent', {});
  } catch (err) {
    return err;
  }
};

export const saveEvent = async (input: CreateEventInput | UpdateEventInput, options?: { forceCreate?: boolean }) => {
  try {
    const { forceCreate = false } = options ?? {};
    if (!forceCreate && input.id !== null) {
      const req = (await API.graphql(
        graphqlOperation(updateEvent, {
          input,
        })
      )) as GraphQLResult<UpdateEventMutation>;
      return req?.data?.updateEvent;
    } else {
      const req = (await API.graphql(
        graphqlOperation(createEvent, {
          input,
        })
      )) as GraphQLResult<CreateEventMutation>;
      return req?.data?.createEvent;
    }
  } catch (err) {
    console.log(err);
    return err;
  }
};

export const saveEventTimes = async (input: CreateEventTimeInput | UpdateEventTimeInput, options?: { forceCreate?: boolean }) => {
  try {
    const { forceCreate = false } = options ?? {};
    if (!forceCreate && input.eventId !== null && input.startDate !== null) {
      const req = (await API.graphql(
        graphqlOperation(updateEventTime, {
          input,
        })
      )) as GraphQLResult<UpdateEventTimeMutation>;
      return req?.data?.updateEventTime;
    } else {
      const req = (await API.graphql(
        graphqlOperation(createEventTime, {
          input,
        })
      )) as GraphQLResult<CreateEventTimeMutation>;
      return req?.data?.createEventTime;
    }
  } catch (err) {
    console.log(err);
    return err;
  }
};

export const saveEventSession = async (input: CreateEventSessionInput | UpdateEventSessionInput, options?: { forceCreate?: boolean }) => {
  try {
    const { forceCreate = false } = options ?? {};
    if (!forceCreate && input.id !== null) {
      const req = (await API.graphql(
        graphqlOperation(updateEventSession, {
          input,
        })
      )) as GraphQLResult<UpdateEventSessionMutation>;
      return req?.data?.updateEventSession;
    } else {
      const req = (await API.graphql(
        graphqlOperation(createEventSession, {
          input,
        })
      )) as GraphQLResult<CreateEventSessionMutation>;
      return req?.data?.createEventSession;
    }
  } catch (err) {
    return err;
  }
};

export const saveEventSessionTimes = async (input: CreateEventSessionTimeInput | UpdateEventSessionTimeInput, options?: { forceCreate?: boolean }) => {
  try {
    const { forceCreate = false } = options ?? {};
    if (!forceCreate && input.eventSessionId !== null && input.startDate !== null) {
      const req = (await API.graphql(
        graphqlOperation(updateEventSessionTime, {
          input,
        })
      )) as GraphQLResult<UpdateEventSessionTimeMutation>;
      return req?.data?.updateEventSessionTime;
    } else {
      const req = (await API.graphql(
        graphqlOperation(createEventSessionTime, {
          input,
        })
      )) as GraphQLResult<CreateEventSessionTimeMutation>;
      return req?.data?.createEventSessionTime;
    }
  } catch (err) {
    return err;
  }
};

export const saveEventSessionFacilitator = async (
  input: CreateEventSessionFacilitatorInput | UpdateEventSessionFacilitatorInput,
  options?: { forceCreate?: boolean }
) => {
  try {
    const { forceCreate = false } = options ?? {};
    if (!forceCreate && input.eventSessionId !== null && input.coachId !== null) {
      const req = (await API.graphql(
        graphqlOperation(updateEventSessionFacilitator, {
          input,
        })
      )) as GraphQLResult<UpdateEventSessionFacilitatorMutation>;
      return req?.data?.updateEventSessionFacilitator;
    } else {
      const req = (await API.graphql(
        graphqlOperation(createEventSessionFacilitator, {
          input,
        })
      )) as GraphQLResult<CreateEventSessionFacilitatorMutation>;
      return req?.data?.createEventSessionFacilitator;
    }
  } catch (err) {
    return err;
  }
};

export const saveEventSessionAttendee = async (
  input: CreateEventSessionAttendeesInput | UpdateEventSessionAttendeesInput,
  options?: { forceCreate?: boolean }
) => {
  try {
    const { forceCreate = false } = options ?? {};
    if (!forceCreate && input.userId !== null && input.eventId !== null) {
      const req = (await API.graphql(
        graphqlOperation(updateEventSessionAttendees, {
          input,
        })
      )) as GraphQLResult<UpdateEventSessionAttendeesMutation>;
      return req?.data?.updateEventSessionAttendees;
    } else {
      const req = (await API.graphql(
        graphqlOperation(createEventSessionAttendees, {
          input,
        })
      )) as GraphQLResult<CreateEventSessionAttendeesMutation>;
      return req?.data?.createEventSessionAttendees;
    }
  } catch (err) {
    return err;
  }
};

export const getAllEventAttendees = async (eventId: string) => {
  try {
    const req = await API.graphql(graphqlOperation(listEventSessionAttendeesByEventId, { eventId }));
    return _.get(req, ['data', 'listEventSessionAttendeesByEventId', 'items'], []);
  } catch (err) {
    return err;
  }
};

export const addEventSessionAttendeeToChatChannel = async (userId: string, eventSession: EventSession) => {
  try {
    if (!userId || !_.has(eventSession, ['eventId'])) throw new Error('Invalid parameters passed');
    // members: [String]!, channelId: String!, channelName: String!, defaultMessage: String, defaultMessageFromUserId: String
    const members = [userId];
    const channelId = `SGDC-${_.get(eventSession, ['id'])}`;
    const channelName = _.get(eventSession, ['sessionTitle'], 'Flourish discussion group');
    const req = await API.graphql(graphqlOperation(addUserToChatChannel, { members, channelId, channelName }));
    return typeof _.get(req, ['data', 'addUserToChatChannel']) === 'string'
      ? JSON.parse(_.get(req, ['data', 'addUserToChatChannel']))
      : _.get(req, ['data', 'addUserToChatChannel']);
  } catch (err) {}
};

export const createEventPaymentIntent = async (userId: string, pricingId: string, metadata: Record<string, unknown>) => {
  if (!userId || userId === '' || !pricingId || pricingId === '') return false;
  try {
    const req = await API.graphql(
      graphqlOperation(getStripePaymentIntent, { pricingId, userId, metadata: typeof metadata !== 'string' ? JSON.stringify(metadata) : metadata })
    );
    return typeof _.get(req, ['data', 'getStripePaymentIntent']) === 'string'
      ? JSON.parse(_.get(req, ['data', 'getStripePaymentIntent']))
      : _.get(req, ['data', 'getStripePaymentIntent']);
  } catch (err) {
    console.log(err);
    return err;
  }
};
